import { useState, useMemo, useRef } from 'react';
import { Modal } from 'antd';
import CreateFallbackModal from './CreateNewFallBackpopUpModal';
import AddFallback from './AddFallback';
import NewDraftFallback from './NewFallback';
import FallbackDetails from './FallbackDetails';
import SearchFilterSection from './SearchFilterSection';
import FallbackList from './FallbackList';
import IgnoreFallbackCompressors from './IgnoreFallbackCompressors';
import { useFallbackData } from './useFallbackData';
import { EngineConfigStatus } from '@/store/Engine/constants';
import { getColumns } from '../Common/columns';
import { MainContainerForFallbackTwoPointO } from './styled';

import { useStores } from '@/hooks/use-stores';
import SpinCentered from '@/comp/SpinCentered';
import { observer } from 'mobx-react-lite';
import { useMeasurement } from '@/hooks/useMeasurement';
import { handleUpdateScenario, handleRemoveScenario, handleAddFallbackSaveSuccess } from './Fallbacks.utils';
import { ModeOfFallbackCreation } from '@/constants/FallbackEnum';
import { useTranslation } from 'react-i18next';
import MetricTooltipContent from '../Common/Range/MetricTooltipContent';

interface FallbackTabProps {
  configurationStatus: EngineConfigStatus;
  fromVersionHistory?: boolean;
  versionNumber?: string;
}

const FallbackTab = ({ configurationStatus, fromVersionHistory, versionNumber }: FallbackTabProps) => {
  const { engineStore, proactiveEngineStore, devicesStore } = useStores();
  const { tvalue } = useMeasurement();
  const { t } = useTranslation();

  const {
    selectedFallback,
    currentTableData,
    filteredList,
    compressors,
    tableName,
    metricsByCompressor,
    appliedFilters,
    inputValue,
    handleCellChange,
    handleInputChange,
    handleSearchSubmit,
    handleSearchClear,
    handleSearchIconClick,
    handleFilterApply,
    handleAppliedFiltersClose,
    handleListItemClick,
    handleViewButtonClick,
    handlePaginationChange,
    setEditableTableData,
  } = useFallbackData(configurationStatus, fromVersionHistory, versionNumber);

  const filterButtonRef = useRef<HTMLDivElement>(null);
  const [isFilterModalOpen, setIsFilterModalOpen] = useState(false);
  const [isCreateFallbackModalOpen, setIsCreateFallbackModalOpen] = useState(false);
  const [showAddFallback, setShowAddFallback] = useState(false);
  const [selectedRangeParentKey, setSelectedRangeParentKey] = useState<string | null>(null);
  const [smartRulesData, setSmartRulesData] = useState(null);
  const [appliedFiltersFromModal, setAppliedFiltersFromModal] = useState([]);
  const [confirmationModalInfo, setConfirmationModalInfo] = useState({
    open: false,
    modalText: undefined,
    actionHandler: undefined,
  });

  const shouldShowNewDraftFallback =
    filteredList.length === 0 &&
    (configurationStatus === EngineConfigStatus.Draft || configurationStatus === EngineConfigStatus.DefaultStrategy);
  const compressorTooltipContents = useMemo(() => {
    const tooltips = {};
    Object.keys(metricsByCompressor).forEach(compressorId => {
      const metrics = metricsByCompressor[compressorId];
      tooltips[compressorId] =
        metrics && metrics.length > 0 ? <MetricTooltipContent metrics={metrics} /> : t('noMetricsAvailable');
    });
    return tooltips;
  }, [metricsByCompressor, t]);

  const onConfirmationModalOpen = (modalText, actionHandler) => () =>
    setConfirmationModalInfo({ open: true, modalText, actionHandler });

  const onConfirmationModalClose = () =>
    setConfirmationModalInfo({ open: false, modalText: undefined, actionHandler: undefined });

  const handleFilterApplyWrapper = filters => {
    handleFilterApply(filters);
    setIsFilterModalOpen(false);
  };

  const handleFilterClose = () => {
    setIsFilterModalOpen(false);
  };

  const handleCreateFallbackModalClose = () => {
    setIsCreateFallbackModalOpen(false);
  };

  const handleCreateFallback = data => {
    setAppliedFiltersFromModal(data.appliedFilters || []);
    if (data.mode === ModeOfFallbackCreation.Existing) {
      setSelectedRangeParentKey(data.rangeParentKey);
      setSmartRulesData(null);
      setIsCreateFallbackModalOpen(false);
      setShowAddFallback(true);
    } else if (data.mode === ModeOfFallbackCreation.Custom) {
      setSelectedRangeParentKey(null);
      setSmartRulesData(data.rulesData);
      setIsCreateFallbackModalOpen(false);
      setShowAddFallback(true);
    }
  };

  const handleAddFallbackClose = () => {
    setShowAddFallback(false);
  };

  const handleAddFallbackSaveSuccessWrapper = () => {
    handleAddFallbackSaveSuccess(appliedFilters, engineStore, configurationStatus, tvalue);
  };

  const handleUpdateScenarioWrapper = async () => {
    await handleUpdateScenario({
      selectedFallback,
      currentTableData,
      tvalue,
      engineStore,
      appliedFilters,
      configurationStatus,
      setEditableTableData,
      devicesMetadata: devicesStore?.devicesMetadata,
      t,
    });
  };

  const handleRemoveScenarioWrapper = async () => {
    await handleRemoveScenario({
      selectedFallback,
      engineStore,
      tvalue,
      appliedFilters,
      configurationStatus,
    });
  };

  let columnsForFallbackTwoPointO =
    configurationStatus === EngineConfigStatus.Draft
      ? getColumns(true, t, handleCellChange, compressorTooltipContents)
      : getColumns(false, t, undefined, compressorTooltipContents);
  let isTableEditable = configurationStatus === EngineConfigStatus.Draft;

  if (selectedFallback && selectedFallback?.scenario?.fallbackRules[0]?.ruleParentKey) {
    columnsForFallbackTwoPointO = getColumns(false, t, undefined, compressorTooltipContents);
    isTableEditable = false;
  }
  if (engineStore.isLoadingNewFallbackScenario) return <SpinCentered />;
  if (engineStore.isLoadingSaveUpdateRemoveFallbackScenario) return <SpinCentered />;
  if (proactiveEngineStore.isLoadingCommandsHistory) return <SpinCentered />;

  if (showAddFallback) {
    return (
      <AddFallback
        onClose={handleAddFallbackClose}
        onSaveSuccess={handleAddFallbackSaveSuccessWrapper}
        rangeParentKey={selectedRangeParentKey}
        smartRulesData={smartRulesData}
        appliedFilters={appliedFiltersFromModal}
      />
    );
  }

  if (shouldShowNewDraftFallback && !inputValue && !appliedFilters.length) {
    return <NewDraftFallback configurationStatus={configurationStatus} />;
  }
  const isDraftMode =
    configurationStatus === EngineConfigStatus.Draft || configurationStatus === EngineConfigStatus.DefaultStrategy;

  return (
    <MainContainerForFallbackTwoPointO>
      {!fromVersionHistory && (
        <SearchFilterSection
          inputValue={inputValue}
          onInputChange={handleInputChange}
          onSearchSubmit={handleSearchSubmit}
          onSearchClear={handleSearchClear}
          onSearchIconClick={handleSearchIconClick}
          isFilterModalOpen={isFilterModalOpen}
          onFilterApply={handleFilterApplyWrapper}
          onFilterClose={handleFilterClose}
          onAppliedFiltersClose={handleAppliedFiltersClose}
          appliedFilters={appliedFilters}
          hasFiltersApplied={appliedFilters.length > 0}
          filterButtonRef={filterButtonRef}
          onFilterModalOpenChange={setIsFilterModalOpen}
          isDraftMode={isDraftMode}
          onRemoveScenario={onConfirmationModalOpen(t('confirmRemoveScenario'), handleRemoveScenarioWrapper)}
          onUpdateScenario={onConfirmationModalOpen(t('confirmUpdateScenario'), handleUpdateScenarioWrapper)}
          onCreateFallback={() => setIsCreateFallbackModalOpen(true)}
          isUpdateLoading={engineStore.isLoadingSaveUpdateRemoveFallbackScenario}
          isUpdateDisabled={!selectedFallback || currentTableData.length === 0 || !isTableEditable}
          totalCount={engineStore.pagination.total}
        />
      )}

      <div className="content-section-fallback-two-point-o">
        <FallbackList
          filteredList={filteredList}
          selectedId={selectedFallback?.id || 0}
          onListItemClick={handleListItemClick}
          onViewButtonClick={handleViewButtonClick}
          compressors={compressors}
          pagination={{
            current: engineStore.pagination.page,
            pageSize: engineStore.pagination.pageSize,
            total: engineStore.pagination.total,
            onChange: handlePaginationChange,
          }}
          isDraftMode={isDraftMode}
          fromVersionHistory={fromVersionHistory}
          configurationStatus={configurationStatus}
        />
        <div className="right-panel-fallback-two-point-o">
          {!fromVersionHistory && <IgnoreFallbackCompressors />}
          <FallbackDetails
            selectedFallback={selectedFallback}
            tableName={tableName}
            columns={columnsForFallbackTwoPointO}
            currentTableData={currentTableData}
            compressors={compressors}
            isDraftMode={isDraftMode}
            fromVersionHistory={fromVersionHistory}
            configurationStatus={configurationStatus}
          />
        </div>
      </div>

      <CreateFallbackModal
        open={isCreateFallbackModalOpen}
        onClose={handleCreateFallbackModalClose}
        onCreateFallback={handleCreateFallback}
      />
      <Modal
        open={!!confirmationModalInfo?.open}
        onOk={confirmationModalInfo?.actionHandler}
        onCancel={onConfirmationModalClose}
        okText={t('ok')}
        cancelText={t('cancel')}
        maskClosable={false}
        closable={false}
      >
        <span>{confirmationModalInfo?.modalText}</span>
      </Modal>
    </MainContainerForFallbackTwoPointO>
  );
};

export default observer(FallbackTab);
